import type {
  GetMessagesQuery,
  GetMessagesQueryVariables,
} from '#src/graphql/generated';

import { NetworkStatus } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  HStack,
  Heading,
  Link,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { ChatComposerRow } from '#src/components/chat-composer-row';
import { CollapsibleJsonReadonlyBlock } from '#src/components/collapsible-json-readonly-block';
import {
  MessageList,
  type ChatMessageQueryOptions,
} from '#src/components/message-list';
import { toaster } from '#src/components/ui/toaster';
import { mergeCreateMessageIntoGetMessagesCache } from '#src/graphql/cache/merge-create-message-into-get-messages';
import { CREATE_AI_THREAD_REPLY } from '#src/graphql/mutations/create-ai-thread-reply';
import { CREATE_MESSAGE } from '#src/graphql/mutations/create-message';
import { DELETE_MESSAGE } from '#src/graphql/mutations/delete-message';
import { EXECUTE_PROCESSOR } from '#src/graphql/mutations/execute-processor';
import { GET_AI_AUDIT_BY_ID } from '#src/graphql/queries/ai-audit';
import { GET_MESSAGES } from '#src/graphql/queries/messages';
import { useThreadFocusSearchParams } from '#src/hooks/use-thread-focus-search-params';
import { resolveFocusParamToRootId } from '#src/utils/message-thread-focus';

const ASK_AI_AUDIT_PROCESSOR_ID = 'ask-ai-audit-prompt';
/** Message context name linking a thread to an `ai_audit` row (see messages GraphQL). */
const AI_AUDIT_MESSAGE_CONTEXT = 'aiAuditId';

type AuditChatTurn = { role: 'user' | 'assistant'; content: string };

type MessageRoot = GetMessagesQuery['messages'][number];

/** Root plus replies; nested GraphQL children omit `childMessages`, so avoid `MessageRoot[]`. */
type ThreadLineMessage = Pick<
  MessageRoot,
  'id' | 'userId' | 'value' | 'createdAt'
>;

function orderedThread(root: MessageRoot): ThreadLineMessage[] {
  const children = [...(root.childMessages ?? [])].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
  return [root, ...children];
}

function messageToTurn(m: { userId: string; value: string }): AuditChatTurn {
  return {
    role: m.userId === 'system' ? 'assistant' : 'user',
    content: m.value,
  };
}

function threadHistoryAsPriorTurns(root: MessageRoot): AuditChatTurn[] {
  return orderedThread(root).map(messageToTurn);
}

function priorMessagesBeforeTarget(
  root: MessageRoot,
  targetMessageId: string,
): AuditChatTurn[] {
  const ordered = orderedThread(root);
  const idx = ordered.findIndex((m) => m.id === targetMessageId);
  if (idx <= 0) return [];
  return ordered.slice(0, idx).map(messageToTurn);
}

function findThreadRootForMessage(
  roots: MessageRoot[],
  messageId: string,
): MessageRoot | null {
  for (const root of roots) {
    if (root.id === messageId) return root;
    for (const child of root.childMessages ?? []) {
      if (child.id === messageId) return root;
    }
  }
  return null;
}

function parseAskAuditAssistantText(result: unknown): string | undefined {
  if (
    result &&
    typeof result === 'object' &&
    'message' in result &&
    typeof (result as { message: unknown }).message === 'string'
  ) {
    return (result as { message: string }).message;
  }
  return undefined;
}

interface AiAuditMessagesPanelProps {
  auditId: string;
  auditInput: unknown;
  auditOutput: unknown;
}

function AiAuditMessagesPanel({
  auditId,
  auditInput,
  auditOutput,
}: AiAuditMessagesPanelProps) {
  const [messageText, setMessageText] = useState('');
  const {
    rawFocusMessageId,
    expandThread,
    collapseThread,
    replaceStripFocusParam,
  } = useThreadFocusSearchParams();

  const getMessagesVariables = useMemo<GetMessagesQueryVariables>(
    () => ({
      input: {
        contextFilters: [{ name: AI_AUDIT_MESSAGE_CONTEXT, value: auditId }],
      },
    }),
    [auditId],
  );

  const [createMessage, { loading: loadingCreate }] =
    useMutation(CREATE_MESSAGE);
  const [executeProcessor, { loading: loadingExec }] =
    useMutation(EXECUTE_PROCESSOR);
  const [createAiThreadReply, { loading: loadingAiReply }] = useMutation(
    CREATE_AI_THREAD_REPLY,
  );
  const [deleteMessage] = useMutation(DELETE_MESSAGE);

  const {
    data,
    error: messagesError,
    networkStatus: messagesNetworkStatus,
    refetch: refetchMessages,
  } = useQuery(GET_MESSAGES, {
    variables: getMessagesVariables,
    fetchPolicy: 'cache-and-network',
  });

  /** Initial load only; user message merged on create; final refetch loads assistant reply. */
  const isMessagesInitialLoad =
    messagesNetworkStatus === NetworkStatus.loading && data == null;

  const sortedMessages = useMemo(
    () =>
      [...(data?.messages ?? [])].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      ),
    [data?.messages],
  );

  const focusedRootId = useMemo(
    () => resolveFocusParamToRootId(rawFocusMessageId, sortedMessages),
    [rawFocusMessageId, sortedMessages],
  );

  const displayMessages = useMemo(
    () =>
      focusedRootId
        ? sortedMessages.filter((m) => m.id === focusedRootId)
        : sortedMessages,
    [sortedMessages, focusedRootId],
  );

  useEffect(() => {
    if (isMessagesInitialLoad) return;
    if (rawFocusMessageId && !focusedRootId) {
      replaceStripFocusParam();
    }
  }, [
    isMessagesInitialLoad,
    rawFocusMessageId,
    focusedRootId,
    replaceStripFocusParam,
  ]);

  const [chatMessageQueryLoadingId, setChatMessageQueryLoadingId] = useState<
    string | null
  >(null);

  const auditContexts = useMemo(
    () => [{ name: AI_AUDIT_MESSAGE_CONTEXT, value: auditId }],
    [auditId],
  );

  const handleRunAskAuditNote = useCallback(
    async (
      messageId: string,
      query: string,
      options?: ChatMessageQueryOptions,
    ) => {
      void options;
      setChatMessageQueryLoadingId(messageId);
      try {
        const root = findThreadRootForMessage(sortedMessages, messageId);
        if (!root) {
          throw new Error('Thread not found');
        }
        const priorMessages = priorMessagesBeforeTarget(root, messageId);
        const { data: execData } = await executeProcessor({
          variables: {
            input: {
              processorId: ASK_AI_AUDIT_PROCESSOR_ID,
              variables: {
                question: query,
                priorMessages,
                auditInput,
                auditOutput,
              },
            },
          },
        });
        const result = execData?.executeProcessor;
        if (!result?.success || result?.result == null) {
          throw new Error(result?.error ?? 'Processor failed');
        }
        const assistantText = parseAskAuditAssistantText(result.result);
        if (typeof assistantText !== 'string') {
          throw new Error('Processor did not return a message');
        }
        try {
          await createAiThreadReply({
            variables: {
              input: {
                rootMessageId: root.id,
                value: assistantText,
              },
            },
          });
        } catch (persistErr) {
          toaster.create({
            title: 'Could not save assistant reply',
            description:
              persistErr instanceof Error
                ? persistErr.message
                : 'Reply was not persisted',
            type: 'warning',
          });
        }
        await refetchMessages();
        toaster.create({
          title: 'AI note added',
          description: 'A reply was added to this thread.',
          type: 'success',
        });
      } catch (error) {
        toaster.create({
          title: 'Could not run AI action',
          description:
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred',
          type: 'error',
        });
        throw error;
      } finally {
        setChatMessageQueryLoadingId(null);
      }
    },
    [
      auditInput,
      auditOutput,
      createAiThreadReply,
      executeProcessor,
      refetchMessages,
      sortedMessages,
    ],
  );

  const handleSendMessage = useCallback(async () => {
    const q = messageText.trim();
    if (!q) return;

    let priorMessages: AuditChatTurn[];
    if (focusedRootId) {
      const root = sortedMessages.find((m) => m.id === focusedRootId);
      priorMessages = root ? threadHistoryAsPriorTurns(root) : [];
    } else {
      priorMessages = [];
    }

    const isFirstTurn = focusedRootId === null;
    const threadRootId = focusedRootId;

    let userMessageId: string | null = null;
    try {
      if (isFirstTurn) {
        const { data: createData } = await createMessage({
          variables: {
            input: {
              value: q,
              contexts: auditContexts,
            },
          },
          update(cache, { data: createMutationData }) {
            if (createMutationData?.createMessage) {
              mergeCreateMessageIntoGetMessagesCache(
                cache,
                createMutationData.createMessage,
                getMessagesVariables,
              );
            }
          },
        });
        userMessageId = createData?.createMessage?.id ?? null;
      } else {
        const { data: createData } = await createMessage({
          variables: {
            input: {
              value: q,
              parentMessageId: threadRootId!,
              contexts: auditContexts,
            },
          },
          update(cache, { data: createMutationData }) {
            if (createMutationData?.createMessage) {
              mergeCreateMessageIntoGetMessagesCache(
                cache,
                createMutationData.createMessage,
                getMessagesVariables,
              );
            }
          },
        });
        userMessageId = createData?.createMessage?.id ?? null;
      }
      if (!userMessageId) {
        throw new Error('Failed to create message');
      }

      const rootForAiReply = isFirstTurn ? userMessageId : threadRootId!;

      const { data: execData } = await executeProcessor({
        variables: {
          input: {
            processorId: ASK_AI_AUDIT_PROCESSOR_ID,
            variables: {
              question: q,
              priorMessages,
              auditInput,
              auditOutput,
            },
          },
        },
      });
      const result = execData?.executeProcessor;
      if (!result?.success || result?.result == null) {
        throw new Error(result?.error ?? 'Processor failed');
      }
      const assistantText = parseAskAuditAssistantText(result.result);
      if (typeof assistantText !== 'string') {
        throw new Error('Processor did not return a message');
      }

      try {
        await createAiThreadReply({
          variables: {
            input: {
              rootMessageId: rootForAiReply,
              value: assistantText,
            },
          },
        });
      } catch (persistErr) {
        toaster.create({
          title: 'Could not save assistant reply',
          description:
            persistErr instanceof Error
              ? persistErr.message
              : 'Reply was not persisted',
          type: 'warning',
        });
      }

      setMessageText('');
      await refetchMessages();
    } catch (err) {
      if (userMessageId) {
        try {
          await deleteMessage({ variables: { input: { id: userMessageId } } });
        } catch {
          /* best-effort rollback */
        }
      }
      toaster.create({
        title: 'Error',
        description:
          err instanceof Error ? err.message : 'Failed to get AI response',
        type: 'error',
      });
    }
  }, [
    auditContexts,
    auditInput,
    auditOutput,
    createAiThreadReply,
    createMessage,
    deleteMessage,
    executeProcessor,
    focusedRootId,
    getMessagesVariables,
    messageText,
    refetchMessages,
    sortedMessages,
  ]);

  const sending = loadingCreate || loadingExec || loadingAiReply;

  return (
    <VStack gap={4} align="stretch" width="100%" flex={1} minH={0}>
      <Box flexShrink={0}>
        <Heading size="md" color="fg">
          Ask AI about this audit
        </Heading>
        <Text fontSize="sm" color="fg.muted" mt={1} maxW="48rem">
          Chat about this logged AI interaction. The model sees the same input
          and output JSON shown on this page (large payloads may be truncated
          server-side). Follow-up messages include this conversation.
        </Text>
      </Box>
      <Box display="flex" flexDirection="column" gap={4} flex={1} minH={0}>
        <Box flex={1} minH={0} overflowY="auto" width="100%">
          <MessageList
            messages={displayMessages}
            loading={isMessagesInitialLoad}
            error={messagesError ?? undefined}
            emptyMessage="No messages yet. Type below and send to start."
            focusedRootId={focusedRootId}
            onExpandThread={expandThread}
            onRunChatMessageQuery={handleRunAskAuditNote}
            chatMessageQueryLoadingId={chatMessageQueryLoadingId}
            onMessageDeleted={() => void refetchMessages()}
          />
        </Box>
        <VStack gap={2} align="stretch" flexShrink={0}>
          {focusedRootId ? (
            <HStack gap={2} align="center" fontSize="xs" color="fg.muted">
              <Text>Replying in thread</Text>
              <Button
                size="xs"
                variant="ghost"
                onClick={() => collapseThread()}
              >
                Cancel
              </Button>
            </HStack>
          ) : null}
          <ChatComposerRow
            autoGrow
            value={messageText}
            onChange={setMessageText}
            onSubmit={() => void handleSendMessage()}
            placeholder="e.g., What model was requested? Summarize the error…"
            disabled={sending}
            loading={sending}
          />
        </VStack>
      </Box>
    </VStack>
  );
}

export function AiAuditDetailPage() {
  const { id } = useParams<{ id: string }>();
  const auditId = id ?? '';

  const { data, loading, error, refetch } = useQuery(GET_AI_AUDIT_BY_ID, {
    variables: { id: auditId },
    skip: !auditId,
  });

  if (!auditId) {
    return (
      <Box>
        <Text color="fg.muted">Missing audit id.</Text>
        <Link asChild color="blue.600" mt={2} display="inline-block">
          <RouterLink to="/ai-audit">Back to list</RouterLink>
        </Link>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <Spinner />
        <Text mt={2}>Loading audit…</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Text color="red.500">Error: {error.message}</Text>
        <Button onClick={() => refetch()} mt={4}>
          Retry
        </Button>
        <Link asChild color="blue.600" mt={2} display="block">
          <RouterLink to="/ai-audit">Back to list</RouterLink>
        </Link>
      </Box>
    );
  }

  const audit = data?.aiAudit;
  if (!audit) {
    return (
      <Box>
        <Heading size="md" mb={2}>
          Not found
        </Heading>
        <Text color="fg.muted">
          No audit row for id{' '}
          <Text as="span" fontFamily="mono">
            {auditId}
          </Text>
          .
        </Text>
        <Link asChild color="blue.600" mt={4} display="inline-block">
          <RouterLink to="/ai-audit">Back to list</RouterLink>
        </Link>
      </Box>
    );
  }

  return (
    <Box p={4} flex={1} minH={0} display="flex" flexDirection="column">
      <VStack gap={6} align="stretch" flex={1} minH={0}>
        <Box flexShrink={0}>
          <Link asChild color="blue.600" mb={2} display="inline-block">
            <RouterLink to="/ai-audit">← AI audit log</RouterLink>
          </Link>
          <Box>
            <Heading size="lg" color="fg">
              Audit detail
            </Heading>
            <Text fontSize="sm" color="fg.muted" fontFamily="mono">
              {audit.id}
            </Text>
            <Text fontSize="sm" color="fg.muted">
              {new Date(audit.createdAt).toLocaleString()}
            </Text>
            <Text fontSize="sm" color="fg.muted" fontFamily="mono">
              {audit.endpoint}
            </Text>
            {audit.context != null && audit.context !== '' ? (
              <Text fontSize="sm" color="fg.muted" fontFamily="mono">
                context: {audit.context}
              </Text>
            ) : null}
            {audit.eventId != null && audit.eventId !== '' ? (
              <Text fontSize="sm" color="fg.muted" fontFamily="mono">
                event id: {audit.eventId}
              </Text>
            ) : (
              <Text fontSize="sm" color="fg.muted">
                event id: —
              </Text>
            )}
            {audit.description != null && audit.description !== '' ? (
              <Text fontSize="sm" color="fg.muted" fontFamily="mono">
                description: {audit.description}
              </Text>
            ) : (
              <Text fontSize="sm" color="fg.muted">
                description: —
              </Text>
            )}
          </Box>
          <Text fontSize="sm" color="fg.muted" maxW="48rem" mt={2}>
            Input is the HTTP request body sent to OpenAI. For document AI edit,
            the{' '}
            <Text as="span" fontFamily="mono">
              model
            </Text>{' '}
            field there is the value used after the server validates{' '}
            <Text as="span" fontFamily="mono">
              variables.model
            </Text>
            . If that differs from what you picked in the UI, the choice was not
            on the allowlist or did not reach the processor. In Output,{' '}
            <Text as="span" fontFamily="mono">
              model
            </Text>{' '}
            is OpenAI&apos;s canonical id and may look like a dated snapshot
            (e.g.{' '}
            <Text as="span" fontFamily="mono">
              gpt-4o-2024-08-06
            </Text>
            ) even when the request used a short alias.
          </Text>
        </Box>
        <Box flexShrink={0}>
          <CollapsibleJsonReadonlyBlock title="Input" value={audit.input} />
        </Box>
        <Box flexShrink={0}>
          <CollapsibleJsonReadonlyBlock title="Output" value={audit.output} />
        </Box>
        <Box flex={1} minH={0} display="flex" flexDirection="column">
          <AiAuditMessagesPanel
            auditId={audit.id}
            auditInput={audit.input}
            auditOutput={audit.output}
          />
        </Box>
      </VStack>
    </Box>
  );
}
